import React from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import MigrationDTO from '@src/adapters/dto/migration/MigrationDTO';
import { formatDate, formatSeconds } from '@src/utils/dateUtils';
import ConsultantName from '@src/components/user/ConsultantName';
import ProcedureName from '@src/components/procedure/ProcedureName';
import UserName from '@src/components/user/UserName';
import { TableSortLabel } from '@mui/material';

interface Props {
  migrations: MigrationDTO[];
  selectedPage: number;
  rowsPerPage: number;
  recordCount: number;
  sort: { key: string; asc: boolean };
  changePage(value: number): void;
  changeRowsPerPage(value: number): void;
  setSelectedMigration(prefrence: MigrationDTO): void;
  setSortKey(key: string): void;
}

function List({
  migrations,
  selectedPage,
  rowsPerPage,
  recordCount,
  sort,
  setSelectedMigration,
  changePage,
  changeRowsPerPage,
  setSortKey,
}: Props) {
  const handleRowClick = (
    event: React.MouseEvent<unknown>,
    migration: MigrationDTO
  ) => {
    setSelectedMigration(migration);
  };

  return (
    <TableContainer>
      <Table
        aria-labelledby="tableTitle"
        aria-label="Migration table"
        sx={{ width: '100%' }}>
        <TableHead>
          <TableRow>
            <TableCell component="th" align="left">
              <TableSortLabel
                active={sort.key === 'consultant'}
                direction={sort.asc ? 'asc' : 'desc'}
                onClick={() => setSortKey('consultant')}>
                Consultant
              </TableSortLabel>
            </TableCell>
            <TableCell component="th" align="left">
              <TableSortLabel
                active={sort.key === 'procedure'}
                direction={sort.asc ? 'asc' : 'desc'}
                onClick={() => setSortKey('procedure')}>
                Procedure
              </TableSortLabel>
            </TableCell>
            <TableCell component="th" align="left">
              <TableSortLabel
                active={sort.key === 'filename'}
                direction={sort.asc ? 'asc' : 'desc'}
                onClick={() => setSortKey('filename')}>
                File Name
              </TableSortLabel>
            </TableCell>
            <TableCell component="th" align="left">
              <TableSortLabel
                active={sort.key === 'owner'}
                direction={sort.asc ? 'asc' : 'desc'}
                onClick={() => setSortKey('owner')}>
                Migrated By
              </TableSortLabel>
            </TableCell>
            <TableCell component="th" align="left">
              <TableSortLabel
                active={sort.key === 'approver'}
                direction={sort.asc ? 'asc' : 'desc'}
                onClick={() => setSortKey('approver')}>
                Approved By
              </TableSortLabel>
            </TableCell>
            <TableCell component="th" align="left">
              <TableSortLabel
                active={sort.key === 'updated'}
                direction={sort.asc ? 'asc' : 'desc'}
                onClick={() => setSortKey('updated')}>
                Updated Date
              </TableSortLabel>
            </TableCell>
            <TableCell component="th" key="Status" align="left">
              <TableSortLabel
                active={sort.key === 'status'}
                direction={sort.asc ? 'asc' : 'desc'}
                onClick={() => setSortKey('status')}>
                Status
              </TableSortLabel>
            </TableCell>
            <TableCell component="th" align="left">
              Card Item Count
            </TableCell>
            <TableCell component="th" align="left">
              Auto Added
            </TableCell>
            <TableCell component="th" align="left">
              Suggestions
            </TableCell>
            <TableCell component="th" align="left">
              Manually Added
            </TableCell>
            <TableCell component="th" align="left">
              Time To Migrate
            </TableCell>
            <TableCell component="th" align="left">
              <TableSortLabel
                active={sort.key === 'releasedAt'}
                direction={sort.asc ? 'asc' : 'desc'}
                onClick={() => setSortKey('releasedAt')}>
                Released At
              </TableSortLabel>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {migrations.map((migration, index) => {
            return (
              <TableRow
                hover
                onClick={(event) => handleRowClick(event, migration)}
                role="checkbox"
                tabIndex={-1}
                key={index}
                sx={{ cursor: 'pointer' }}>
                <TableCell scope="row">
                  <ConsultantName id={migration.consultantId} />
                </TableCell>
                <TableCell scope="row">
                  <ProcedureName id={migration.procedureId} />
                </TableCell>
                <TableCell scope="row">
                  {migration.originalFilename.replace('.pdf', '')}
                </TableCell>
                <TableCell scope="row">
                  <UserName id={migration.ownerId} firstNameFirst={true} />
                </TableCell>
                <TableCell scope="row">
                  <UserName id={migration.approvedById} firstNameFirst={true} />
                </TableCell>
                <TableCell scope="row">
                  {formatDate(migration.updatedAt, 'DD/MM/YYYY HH:mm:ss')}
                </TableCell>
                <TableCell scope="row">
                  {migration.status.replace(/([A-Z])/g, ' $1').trim()}
                </TableCell>
                <TableCell scope="row" align="center">
                  {migration.itemCount}
                </TableCell>
                <TableCell scope="row" align="center">
                  {migration.itemCount === 0
                    ? '0 (0%)'
                    : `${migration.counts.autoMatch} (${(
                        (migration.counts.autoMatch / migration.itemCount) *
                        100
                      ).toFixed(0)}%)`}
                </TableCell>
                <TableCell scope="row" align="center">
                  {migration.itemCount === 0
                    ? '0 (0%)'
                    : `${migration.counts.suggestionAdd} (${(
                        (migration.counts.suggestionAdd / migration.itemCount) *
                        100
                      ).toFixed(0)}%)`}
                </TableCell>
                <TableCell scope="row" align="center">
                  {migration.itemCount === 0
                    ? '0 (0%)'
                    : `${migration.counts.manualAdd} (${(
                        (migration.counts.manualAdd / migration.itemCount) *
                        100
                      ).toFixed(0)}%)`}
                </TableCell>
                <TableCell scope="row" align="center">
                  {formatSeconds(
                    migration.secondsToMigrate ? migration.secondsToMigrate : 0
                  )}
                </TableCell>
                <TableCell scope="row">
                  {migration.releasedAt &&
                    formatDate(migration.releasedAt, 'DD/MM/YYYY HH:mm:ss')}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        count={recordCount}
        rowsPerPage={rowsPerPage}
        page={selectedPage}
        onPageChange={(event, number) => changePage(number)}
        onRowsPerPageChange={(event) => {
          changeRowsPerPage(parseInt(event.target.value, 10));
          changePage(0);
        }}
      />
    </TableContainer>
  );
}

export default List;
